import { ActionBar, FilterToolbar, PageCanvas } from "@/components/page-canvas";
import { Head, Link, usePage } from "@inertiajs/react";
import { useTranslation } from "react-i18next";
import AppLayout from "@/layouts/app_layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  RecordMetadata,
  RecordRow,
  RecordTitle,
} from "@/components/record-link";
import {
  newLocationCustomerPath,
  locationCustomerPath,
  locationCustomersPath,
} from "@/routes";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { CustomerProfile } from "./types";

export default function Customers({
  customers,
  pagination,
  query = "",
}: {
  customers: CustomerProfile[];
  query?: string;
  pagination: { page: number; pages: number };
}) {
  const { auth } = usePage().props;
  const { t } = useTranslation();
  if (!auth.location) return null;
  const locationId = auth.location.id;
  return (
    <AppLayout
      breadcrumbs={[
        {
          title: t("customers.title"),
          href: locationCustomersPath(locationId),
        },
      ]}
    >
      <Head title={t("customers.title")} />
      <PageCanvas>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <h1 className="text-page font-display">{t("customers.title")}</h1>
          <Button render={<Link href={newLocationCustomerPath(locationId)} />}>
            {t("customers.create")}
          </Button>
        </div>
        <FilterToolbar
          action={locationCustomersPath(locationId)}
          method="get"
          columns="search"
        >
          <div className="space-y-2">
            <Label htmlFor="customer-search">
              {t("customer_workspace.search")}
            </Label>
            <Input
              id="customer-search"
              name="q"
              type="search"
              defaultValue={query}
              maxLength={200}
            />
          </div>
          <ActionBar>
            <Button type="submit" variant="outline">
              {t("job_board.search_button")}
            </Button>
            {query && (
              <Link
                href={locationCustomersPath(locationId)}
                className="underline"
              >
                {t("jobs.creation.clear_search")}
              </Link>
            )}
          </ActionBar>
        </FilterToolbar>
        <Card>
          <CardContent className="divide-y">
            {customers.length === 0 && (
              <p className="text-muted-foreground py-6 text-sm">
                {t("customers.empty")}
              </p>
            )}
            {customers.map((customer) => (
              <RecordRow
                key={customer.id}
                className="-mx-3 py-4"
                href={locationCustomerPath(locationId, customer.id)}
              >
                <RecordTitle>{customer.name}</RecordTitle>
                <RecordMetadata className="mt-1">
                  {[customer.email, customer.phone].filter(Boolean).join(" · ")}
                </RecordMetadata>
              </RecordRow>
            ))}
          </CardContent>
        </Card>
        {pagination.pages > 1 && (
          <nav
            aria-label={t("customers.pages")}
            className="flex flex-wrap items-center justify-between gap-3"
          >
            <p className="text-muted-foreground text-sm">
              {t("customers.page", pagination)}
            </p>
            <div className="flex gap-3">
              {pagination.page > 1 && (
                <Button
                  variant="outline"
                  render={
                    <Link
                      href={locationCustomersPath(locationId, {
                        page: pagination.page - 1,
                        q: query || undefined,
                      })}
                    />
                  }
                >
                  {t("customers.previous")}
                </Button>
              )}
              {pagination.page < pagination.pages && (
                <Button
                  variant="outline"
                  render={
                    <Link
                      href={locationCustomersPath(locationId, {
                        page: pagination.page + 1,
                        q: query || undefined,
                      })}
                    />
                  }
                >
                  {t("customers.next")}
                </Button>
              )}
            </div>
          </nav>
        )}
      </PageCanvas>
    </AppLayout>
  );
}
